import { useNavigate } from 'react-router-dom';
import { Building, BarChart3, ClipboardCheck, FileText, DollarSign, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import StatCard from '../../components/ui/StatCard';
import { useOwnerDashboard } from '../../hooks/useDashboard';
import { RecentActivityFeed } from '../../components/ui/RecentActivityFeed';
import { DashboardSkeleton } from '../../components/design-system/EmptyState';

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, isError, error, refetch } = useOwnerDashboard();

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="flex items-center gap-2 text-coral-600">
          <AlertCircle size={20} />
          <span className="text-sm font-medium">{(error as Error)?.message || 'Failed to load dashboard data. Please try again.'}</span>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-10 text-sm font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-6">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900 leading-tight">Portfolio Overview</h1>
          <p className="text-[13px] text-ink-500 mt-0.5">Welcome back, {user?.firstName || user?.name || 'there'}</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Properties"    value={data?.totalProperties ?? 0}    icon={<Building size={14} />} />
        <StatCard label="Active Leases"       value={data?.activeLeases ?? 0}       tone="teal" />
        <StatCard label="Pending Payments"    value={data?.pendingPayments ?? 0}    tone={data?.pendingPayments ? 'warn' : 'default'} />
        <StatCard label="Open Maintenance"    value={data?.pendingMaintenance ?? 0} tone={data?.pendingMaintenance ? 'danger' : 'default'} />
      </div>

      {/* Quick actions */}
      <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
        <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {([
            { icon: <Building size={18} />,        label: 'Ledger',     to: '/owner/ledger' },
            { icon: <BarChart3 size={18} />,        label: 'Tax Reports',to: '/owner/tax-reports' },
            { icon: <ClipboardCheck size={18} />,   label: 'Compliance', to: '/owner/compliance' },
            { icon: <FileText size={18} />,         label: 'Leases',     to: '/owner/leases' },
            { icon: <DollarSign size={18} />,       label: 'Properties', to: '/owner/properties' },
          ] as const).map(a => (
            <button
              key={a.to}
              onClick={() => navigate(a.to)}
              className="flex flex-col items-center gap-2 p-4 rounded-10 border border-ink-200 text-[13px] font-medium text-ink-800 hover:border-forest-900 hover:bg-forest-50 transition-colors"
            >
              <span className="text-forest-900">{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Portfolio / Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider">Portfolio Summary</h2>
            <button onClick={() => navigate('/owner/properties')} className="text-[12px] text-forest-900 font-medium hover:underline">
              View all
            </button>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Occupied Properties', pct: 85, tone: 'teal' as const },
              { label: 'Lease Renewals Due', pct: 30, tone: 'warn' as const },
              { label: 'Compliance Score',   pct: 92, tone: 'forest' as const },
            ].map(r => (
              <div key={r.label}>
                <div className="flex justify-between mb-1">
                  <span className="text-[12px] text-ink-700">{r.label}</span>
                  <span className="text-[12px] font-medium text-ink-900 tabular-nums">{r.pct}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-ink-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${r.tone === 'teal' ? 'bg-teal-400' : r.tone === 'warn' ? 'bg-warn-500' : 'bg-forest-900'}`}
                    style={{ width: `${r.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <RecentActivityFeed limit={5} />
      </div>
    </div>
  );
};

export default OwnerDashboard;
